import clsx from 'clsx';
import { BookOpen, Bot, CalendarClock, GitBranch, LayoutGrid, Layers, Tags, Users } from 'lucide-react';
import type { BlueprintPlan, BlueprintRegistry } from '@/services/blueprints.service';
import { BOT_MODE_META } from '@/services/blueprints.service';
import { PAGE_WORD } from '@/pages/blueprints/blueprint-meta';

/**
 * What a blueprint gives (or would still add to) a company — used before
 * creating a company and on its detail page before re-applying.
 */
export function BlueprintPlanSummary({ plan, registry, mode }: {
  plan: BlueprintPlan;
  registry: BlueprintRegistry | null;
  /** 'new' shows everything; 'update' shows only what re-applying adds. */
  mode: 'new' | 'update';
}) {
  // In the family's own words — a Food company's catalog shows as "Menu".
  const pageName = (k: string) => (PAGE_WORD[k] && plan.labels[PAGE_WORD[k]]?.plural) || registry?.pages.find((p) => p.key === k)?.label || k;
  const renamed = Object.entries(plan.labels);
  const rows: Array<{ icon: React.ReactNode; title: string; body: React.ReactNode; empty?: boolean }> = [];

  if (mode === 'new') {
    rows.push({
      icon: <LayoutGrid size={14} />,
      title: 'Available pages (subject to module access)',
      body: plan.pages.on.length || plan.pages.off.length
        ? <>{plan.pages.on.length > 0 && <>Adds {plan.pages.on.map(pageName).join(', ')}</>}{plan.pages.on.length > 0 && plan.pages.off.length > 0 && '. '}{plan.pages.off.length > 0 && <>Hides {plan.pages.off.map(pageName).join(', ')}</>}</>
        : 'Standard pages',
    });
    rows.push({
      icon: <Tags size={14} />,
      title: 'Words',
      body: renamed.length
        ? renamed.map(([k, v]) => `${registry?.labels[k]?.plural ?? k} → ${v.plural}`).join(' · ')
        : 'Standard words',
    });
  }

  const fromBlueprint = plan.fields.add.filter((f) => f.from === 'blueprint').length;
  const fromType = plan.fields.add.length - fromBlueprint;
  rows.push({
    icon: <Layers size={14} />,
    title: 'Fields',
    empty: !plan.fields.add.length,
    body: plan.fields.add.length
      ? `${plan.fields.add.length} new${fromType ? ` (${fromBlueprint} from the blueprint, ${fromType} from the type)` : ''}: ${plan.fields.add.slice(0, 6).map((f) => f.name).join(', ')}${plan.fields.add.length > 6 ? '…' : ''}`
      : 'Nothing new',
  });
  rows.push({
    icon: <GitBranch size={14} />,
    title: 'Pipeline',
    empty: plan.pipeline.action === 'none',
    body: plan.pipeline.action === 'create'
      ? `${plan.pipeline.name} with ${plan.pipeline.addStages.length} stages${plan.pipeline.makeDefault ? ' — the default pipeline' : ''}`
      : plan.pipeline.action === 'extend'
        ? `${plan.pipeline.addStages.length} new stage${plan.pipeline.addStages.length === 1 ? '' : 's'} in ${plan.pipeline.name}: ${plan.pipeline.addStages.join(', ')}`
        : 'Nothing new',
  });
  rows.push({
    icon: <Users size={14} />,
    title: 'Departments',
    empty: !plan.departments.add.length,
    body: plan.departments.add.length ? plan.departments.add.join(', ') : 'Nothing new',
  });
  rows.push({
    icon: <CalendarClock size={14} />,
    title: 'Booking drafts',
    empty: !plan.services.add.length,
    body: plan.services.add.length ? plan.services.add.map((s) => `${s.name} (${s.durationMinutes} min)`).join(', ') : 'Nothing new',
  });
  const catalogAdd = plan.catalog?.add ?? [];
  rows.push({
    icon: <BookOpen size={14} />,
    title: 'Catalog items',
    empty: !catalogAdd.length,
    body: catalogAdd.length
      ? `${catalogAdd.length} inactive sample item${catalogAdd.length === 1 ? '' : 's'}: ${catalogAdd.slice(0, 6).map((i) => i.name).join(', ')}${catalogAdd.length > 6 ? '…' : ''}`
      : 'Nothing new',
  });
  if (plan.launchDefaults) {
    rows.push({ icon: <LayoutGrid size={14} />, title: 'Module defaults', body: <>New companies receive the industry modules available on their plan. Existing module access stays as assigned.{plan.launchDefaults.unavailableModules.length > 0 && <span className="block mt-1">Not assigned: {plan.launchDefaults.unavailableModules.join(', ')}. Manage access in Modules.</span>}</> });
    rows.push({ icon: <Bot size={14} />, title: 'Business profile & AI', body: 'Adds missing profile and AI defaults. Existing instructions, model choices and reply modes are preserved. Review menu prices and booking hours before enabling them.' });
    rows.push({ icon: <BookOpen size={14} />, title: 'WhatsApp draft templates', body: `${plan.launchDefaults.templateNames.join(', ')}. Copied per account, including numbers connected later. Review and submit for Meta approval.` });
    rows.push({ icon: <LayoutGrid size={14} />, title: 'Dashboard', body: `${plan.launchDefaults.dashboardWidgets.join(', ')} — actual records, with chart controls.` });
  }
  if (mode === 'new' || plan.botMode.willSet) {
    rows.push({ icon: <Bot size={14} />, title: 'WhatsApp reply mode', body: BOT_MODE_META[plan.botMode.value]?.label ?? plan.botMode.value });
  }

  return (
    <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
      {rows.map((r) => (
        <li key={r.title} className="flex gap-3 px-3.5 py-2.5">
          <span className={clsx('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md', r.empty ? 'bg-surface-alt text-text-muted' : 'bg-primary-soft text-primary')}>{r.icon}</span>
          <div className="min-w-0 text-xs">
            <div className="font-semibold text-text-primary">{r.title}</div>
            <div className={clsx('mt-0.5 leading-relaxed', r.empty ? 'text-text-muted' : 'text-text-secondary')}>{r.body}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}
